import {
  readWorkflowPublishedInspection,
  type WorkflowPublishedInspection,
  type WorkflowPublishedInspectionSource,
  type WorkflowSavedPresence,
} from './workflow-published-inspection';
import type {
  WorkflowPublishedWorkFact,
  WorkflowPublishedWorkDetailRow,
  WorkflowPublishedWorkDetailGroup,
  WorkflowPublishedWorkRow,
  WorkflowPublishedWorkState,
  WorkflowPublishedWorkSection,
  WorkflowPublishedWorkInspection,
} from './workflow-published-work-inspection';

export type WorkflowPublishedStepPolicySectionId = 'timers' | 'relatedWork';
export type WorkflowPublishedStepPolicyFact = WorkflowPublishedWorkFact;
export type WorkflowPublishedStepPolicyDetailRow =
  WorkflowPublishedWorkDetailRow;
export type WorkflowPublishedStepPolicyDetailGroup =
  WorkflowPublishedWorkDetailGroup;
export type WorkflowPublishedStepPolicyRow = WorkflowPublishedWorkRow;
export type WorkflowPublishedStepPolicyState = WorkflowPublishedWorkState;
export interface WorkflowPublishedStepPolicySection extends Omit<
  WorkflowPublishedWorkSection,
  'id'
> {
  id: WorkflowPublishedStepPolicySectionId;
}
export interface WorkflowPublishedStepPolicyInspection {
  context: string;
  state: 'available' | 'unavailable';
  provenance: WorkflowPublishedInspection['provenance'];
  target: { nodeIndex: number; nodeId: string | null };
  node: WorkflowPublishedWorkInspection['node'];
  issues: readonly string[];
  sections: Readonly<
    Record<
      WorkflowPublishedStepPolicySectionId,
      WorkflowPublishedStepPolicySection
    >
  >;
}

type Saved = Record<string, unknown>;
type Field = readonly [string, string];
const record = (raw: unknown): raw is Saved =>
  !!raw && typeof raw === 'object' && !Array.isArray(raw);
const object = (raw: unknown): Saved => (record(raw) ? raw : {});
const presence = (owner: Saved, key: string): WorkflowSavedPresence =>
  !Object.hasOwn(owner, key)
    ? 'absent'
    : owner[key] === null
      ? 'null'
      : 'value';
const copy = (raw: unknown): unknown => structuredClone(raw);
const text = (raw: unknown): raw is string =>
  typeof raw === 'string' && !!raw.trim();
const positive = (raw: unknown): raw is number =>
  typeof raw === 'number' && Number.isSafeInteger(raw) && raw > 0;
const literal = (raw: unknown, values: readonly string[]): boolean =>
  typeof raw === 'string' && values.includes(raw);
function display(raw: unknown): string {
  if (raw === undefined) return 'Not declared';
  if (raw === null) return 'Explicitly null';
  if (typeof raw === 'string') return raw === '' ? 'Empty saved text' : raw;
  try {
    return JSON.stringify(raw) ?? 'Unsupported saved value';
  } catch {
    return 'Unsupported saved value';
  }
}
function facts(
  raw: unknown,
  fields: readonly Field[],
  prefix: string,
): WorkflowPublishedStepPolicyFact[] {
  const value = object(raw);
  return fields.map(([key, label]) => ({
    id: `${prefix}:${key}`,
    label,
    presence: presence(value, key),
    value: display(value[key]),
    issues: [],
    raw: copy(value[key]),
  }));
}
function extraIssues(raw: unknown, fields: readonly string[]): string[] {
  return Object.keys(object(raw)).some((key) => !fields.includes(key))
    ? [
        'Additional saved properties are retained without interpreting their meaning.',
      ]
    : [];
}
function duplicateIssues(
  raw: unknown,
  index: number,
  all: readonly unknown[],
  key: string,
  label: string,
): string[] {
  const value = object(raw)[key];
  if (!text(value))
    return [
      `Saved ${label} at index ${index} is missing or has an unsupported shape.`,
    ];
  return all.filter((item) => object(item)[key] === value).length === 1
    ? []
    : [
        `Duplicate saved ${label}: ${value}. Every indexed declaration is retained independently.`,
      ];
}
function detail(
  raw: unknown,
  index: number,
  prefix: string,
  label: string,
  fields: readonly Field[],
  issues: readonly string[] = [],
): WorkflowPublishedStepPolicyDetailRow {
  return {
    id: `${prefix}-${index}`,
    index,
    label,
    fields: facts(raw, fields, `${prefix}-${index}`),
    issues: [
      ...(!record(raw)
        ? [
            'This saved entry has an unsupported shape. Its original value is retained.',
          ]
        : []),
      ...issues,
    ],
    raw: copy(raw),
  };
}
function objectGroup(
  owner: Saved,
  key: string,
  label: string,
  prefix: string,
  fields: readonly Field[],
  issues: readonly string[] = [],
): WorkflowPublishedStepPolicyDetailGroup {
  const raw = owner[key],
    savedPresence = presence(owner, key);
  const rows =
    savedPresence === 'value'
      ? [
          detail(raw, 0, `${prefix}:${key}`, label, fields, [
            ...issues,
            ...extraIssues(
              raw,
              fields.map(([name]) => name),
            ),
          ]),
        ]
      : [];
  return {
    id: `${prefix}:${key}`,
    label,
    presence: savedPresence,
    state:
      savedPresence !== 'value'
        ? 'absent'
        : rows.some((row) => row.issues.length)
          ? 'retained-unknown'
          : 'available',
    rows,
    issues: [],
    raw: copy(raw),
  };
}
function collection(
  owner: Saved,
  key: string,
  label: string,
  prefix: string,
  project: (
    raw: unknown,
    index: number,
    all: readonly unknown[],
  ) => WorkflowPublishedStepPolicyDetailRow,
): WorkflowPublishedStepPolicyDetailGroup {
  const raw = owner[key],
    savedPresence = presence(owner, key),
    rows = Array.isArray(raw)
      ? raw.map((item, index) => project(item, index, raw))
      : [];
  const issues =
    savedPresence === 'value' && !Array.isArray(raw)
      ? [
          'This saved collection has an unsupported shape. Its original value is retained.',
        ]
      : [];
  return {
    id: `${prefix}:${key}`,
    label,
    presence: savedPresence,
    state:
      savedPresence !== 'value'
        ? 'absent'
        : issues.length || rows.some((row) => row.issues.length)
          ? 'retained-unknown'
          : rows.length
            ? 'available'
            : 'empty',
    rows,
    issues,
    raw: copy(raw),
  };
}
function strings(
  owner: Saved,
  key: string,
  label: string,
  prefix: string,
): WorkflowPublishedStepPolicyDetailGroup {
  return collection(owner, key, label, prefix, (raw, index, all) => ({
    id: `${prefix}:${key}-${index}`,
    index,
    label: `Saved value ${index + 1}`,
    fields: [
      {
        id: `${prefix}:${key}-${index}:value`,
        label: 'Value',
        presence: 'value',
        value: display(raw),
        issues: [],
        raw: copy(raw),
      },
    ],
    issues: [
      ...(!text(raw) ? ['This saved value is not nonempty text.'] : []),
      ...(text(raw) && all.filter((item) => item === raw).length > 1
        ? [
            `Duplicate saved value: ${raw}. Its order and occurrences are retained.`,
          ]
        : []),
    ],
    raw: copy(raw),
  }));
}
function commandGroup(
  action: Saved,
  prefix: string,
): WorkflowPublishedStepPolicyDetailGroup {
  const raw = action['command'],
    value = object(raw),
    key = object(value['key']);
  const group = objectGroup(action, 'command', 'Saved exact command', prefix, [
    ['key', 'Capability key'],
    ['inputSchemaHash', 'Saved input schema hash'],
    ['outputSchemaHash', 'Saved output schema hash'],
    ['contractHash', 'Saved contract hash'],
  ]);
  if (group.rows.length) {
    const row = group.rows[0];
    row.fields = [
      ...facts(
        key,
        [
          ['provider', 'Provider'],
          ['kind', 'Kind'],
          ['code', 'Code'],
          ['version', 'Version'],
        ],
        `${prefix}:command:key`,
      ),
      ...row.fields,
    ];
    if (
      !record(raw) ||
      !record(value['key']) ||
      !text(key['provider']) ||
      key['kind'] !== 'command' ||
      !text(key['code']) ||
      !positive(key['version']) ||
      !['inputSchemaHash', 'outputSchemaHash', 'contractHash'].every((name) =>
        text(value[name]),
      )
    )
      row.issues = [
        ...row.issues,
        'This saved command reference has an unsupported shape. Its exact original value is retained.',
      ];
    row.issues = [
      ...row.issues,
      ...extraIssues(key, ['provider', 'kind', 'code', 'version']),
    ];
    if (row.issues.length) group.state = 'retained-unknown';
  }
  return group;
}
const actionFields: readonly Field[] = [
  ['id', 'Action ID'],
  ['typeCode', 'Action type'],
  ['group', 'Group'],
  ['order', 'Order'],
  ['mode', 'Execution mode'],
  ['failurePolicy', 'Failure policy'],
  ['caller', 'Caller'],
  ['requiredForProgression', 'Required for progression'],
];
function actionGroups(
  timer: Saved,
  key: 'warningAction' | 'breachAction',
  label: string,
  prefix: string,
): WorkflowPublishedStepPolicyDetailGroup[] {
  const raw = timer[key],
    value = object(raw),
    actionPrefix = `${prefix}:${key}`,
    savedPresence = presence(timer, key);
  const header = objectGroup(timer, key, label, prefix, actionFields);
  if (savedPresence !== 'value') return [header];
  if (record(raw)) {
    header.rows[0].issues = [
      ...extraIssues(raw, [
        ...actionFields.map(([name]) => name),
        'command',
        'inputs',
        'outcomes',
      ]),
      ...(!text(value['id']) ||
      value['typeCode'] !== 'command' ||
      value['mode'] !== 'durable' ||
      !text(value['failurePolicy']) ||
      value['caller'] !== 'scoped-system' ||
      typeof value['order'] !== 'number' ||
      !Number.isSafeInteger(value['order']) ||
      typeof value['requiredForProgression'] !== 'boolean' ||
      !record(value['command']) ||
      !Array.isArray(value['inputs']) ||
      !Array.isArray(value['outcomes'])
        ? [
            'The saved action identity, command, collections or policy values have an unsupported shape.',
          ]
        : []),
    ];
    header.state = header.rows[0].issues.length
      ? 'retained-unknown'
      : 'available';
  }
  const inputs = collection(
    value,
    'inputs',
    'Saved action inputs',
    actionPrefix,
    (item, index, all) => {
      const fields: readonly Field[] = [
        ['field', 'Field'],
        ['source', 'Source'],
        ['value', 'Saved literal value'],
        ['context', 'Context field'],
        ['literalJson', 'Saved literal JSON'],
      ];
      const input = object(item),
        issues = [
          ...duplicateIssues(item, index, all, 'field', 'input field'),
          ...(!text(input['source'])
            ? ['The saved input source has an unsupported shape.']
            : []),
          ...extraIssues(
            input,
            fields.map(([name]) => name),
          ),
        ];
      return detail(
        item,
        index,
        `${actionPrefix}:input`,
        text(input['field'])
          ? display(input['field'])
          : `Saved input ${index + 1}`,
        fields,
        issues,
      );
    },
  );
  return [
    header,
    commandGroup(value, actionPrefix),
    inputs,
    strings(value, 'outcomes', 'Saved action outcomes', actionPrefix),
  ];
}
const timerFields: readonly Field[] = [
  ['version', 'Rule version'],
  ['id', 'Timer ID'],
  ['label', 'Timer name'],
  ['startCode', 'Start policy'],
  ['durationUnit', 'Duration unit'],
  ['durationAmount', 'Duration amount'],
  ['dueLocalTime', 'Saved local time'],
  ['gapCode', 'Gap policy'],
  ['overlapCode', 'Overlap policy'],
  ['pauseCode', 'Pause policy'],
  ['resetCode', 'Reset policy'],
  ['reentryCode', 'Re-entry policy'],
  ['warningLeadMinutes', 'Warning lead minutes'],
];
function timerRow(
  raw: unknown,
  index: number,
  all: readonly unknown[],
): WorkflowPublishedStepPolicyRow {
  const value = object(raw),
    prefix = `timer-${index}`,
    issues = [
      ...duplicateIssues(raw, index, all, 'id', 'timer ID'),
      ...extraIssues(raw, [
        ...timerFields.map(([name]) => name),
        'calendar',
        'warningAction',
        'breachAction',
      ]),
    ];
  if (!text(value['label']) || !positive(value['durationAmount']))
    issues.push('The saved timer name or duration has an unsupported shape.');
  if (
    !literal(value['startCode'], ['on_entry']) ||
    !literal(value['durationUnit'], [
      'elapsed_minutes',
      'calendar_days',
      'working_days',
    ]) ||
    !literal(value['pauseCode'], ['disabled', 'manual_before_warning']) ||
    !literal(value['resetCode'], ['disabled', 'restart_from_now']) ||
    !literal(value['reentryCode'], ['restart_new_occurrence'])
  )
    issues.push(
      'A saved timer policy is not interpreted by this reader. Its original literal is retained.',
    );
  const version = value['version'],
    warningLead = value['warningLeadMinutes'] != null,
    warningAction = value['warningAction'] != null;
  if (version !== 1 && version !== 2)
    issues.push(
      'This saved timer rule version is unknown. Its values are retained without interpreting optional-stage semantics.',
    );
  else if (
    version === 1 &&
    (!warningLead || !warningAction || value['breachAction'] == null)
  )
    issues.push(
      'This saved version 1 declaration is missing a required stage value. No stage is generated.',
    );
  else if (
    version === 2 &&
    (warningLead !== warningAction ||
      (!warningAction && value['pauseCode'] === 'manual_before_warning'))
  )
    issues.push(
      'The saved optional warning declaration is inconsistent. No warning or pause policy is inferred.',
    );
  if (warningLead && !positive(value['warningLeadMinutes']))
    issues.push(
      'The saved warning lead is not a positive whole number. Its raw value is retained.',
    );
  if (
    value['dueLocalTime'] != null &&
    typeof value['dueLocalTime'] !== 'string'
  )
    issues.push(
      'The saved local-time value has an unsupported shape; no time is calculated.',
    );
  const base = detail(
    raw,
    index,
    'timer',
    text(value['label']) ? display(value['label']) : `Saved timer ${index + 1}`,
    timerFields,
    issues,
  );
  const calendar = object(value['calendar']);
  const calendarIssues =
    value['calendar'] != null &&
    (!record(value['calendar']) ||
      !['code', 'versionCode', 'contentHash'].every((key) =>
        text(calendar[key]),
      ))
      ? [
          'The saved calendar reference is incomplete or opaque. No current calendar is substituted.',
        ]
      : [];
  const groups = [
    objectGroup(
      value,
      'calendar',
      'Saved exact calendar',
      prefix,
      [
        ['code', 'Calendar code'],
        ['versionCode', 'Calendar version'],
        ['contentHash', 'Saved calendar content hash'],
      ],
      calendarIssues,
    ),
    ...actionGroups(value, 'warningAction', 'Saved warning action', prefix),
    ...actionGroups(value, 'breachAction', 'Saved overdue action', prefix),
  ];
  return {
    ...base,
    description:
      'Saved timer configuration only. No dates, stage, calendar result or execution availability are calculated.',
    state:
      base.issues.length ||
      groups.some((group) => group.state === 'retained-unknown')
        ? 'retained-unknown'
        : 'available',
    groups,
  };
}
const relatedFields: readonly Field[] = [
  ['version', 'Link version'],
  ['id', 'Relationship ID'],
  ['label', 'Name'],
  ['mode', 'Mode'],
  ['returnCode', 'Return policy'],
  ['cancelCode', 'Cancellation policy'],
  ['failureCode', 'Failure policy'],
  ['maximumDepth', 'Maximum depth'],
];
function relatedRow(
  raw: unknown,
  index: number,
  all: readonly unknown[],
): WorkflowPublishedStepPolicyRow {
  const value = object(raw),
    prefix = `related-work-${index}`,
    issues = [
      ...duplicateIssues(raw, index, all, 'id', 'relationship ID'),
      ...extraIssues(raw, [
        ...relatedFields.map(([name]) => name),
        'child',
        'acceptedOutcomeCodes',
      ]),
    ];
  if (value['version'] !== 1)
    issues.push(
      'This saved relationship version is not interpreted by this reader. Its original values are retained.',
    );
  if (!text(value['label']) || !positive(value['maximumDepth']))
    issues.push(
      'The saved relationship name or depth has an unsupported shape.',
    );
  if (
    !literal(value['mode'], ['blocking', 'observational']) ||
    value['returnCode'] !== 'retain_original_visit' ||
    value['cancelCode'] !== 'stop_pending_leave_running' ||
    value['failureCode'] !== 'blocked'
  )
    issues.push(
      'A saved relationship policy is unknown. No replacement or inherited behavior is inferred.',
    );
  const child = object(value['child']);
  const pinIssues =
    !text(child['workflowCode']) ||
    !text(child['definitionVersionCode']) ||
    !text(child['contentHash'])
      ? [
          'The saved child reference is incomplete or opaque. No current child definition is substituted.',
        ]
      : [];
  issues.push(...pinIssues);
  if (!Array.isArray(value['acceptedOutcomeCodes']))
    issues.push(
      'Saved accepted results have an unsupported shape. No child results are inferred.',
    );
  const groups = [
    objectGroup(
      value,
      'child',
      'Saved exact child definition',
      prefix,
      [
        ['workflowCode', 'Workflow code'],
        ['definitionVersionCode', 'Saved version'],
        ['contentHash', 'Saved child content hash'],
      ],
      pinIssues,
    ),
    strings(value, 'acceptedOutcomeCodes', 'Saved accepted results', prefix),
  ];
  const base = detail(
    raw,
    index,
    'related-work',
    text(value['label'])
      ? display(value['label'])
      : `Saved relationship ${index + 1}`,
    relatedFields,
    issues,
  );
  return {
    ...base,
    description:
      'Saved independent child reference and policies only. No child lookup, launch, result acceptance or inherited data is performed.',
    state:
      base.issues.length ||
      groups.some((group) => group.state === 'retained-unknown')
        ? 'retained-unknown'
        : 'available',
    groups,
  };
}
function section(
  behavior: Saved,
  id: WorkflowPublishedStepPolicySectionId,
): WorkflowPublishedStepPolicySection {
  const key = id === 'timers' ? 'timers' : 'childLinks',
    raw = behavior[key],
    savedPresence = presence(behavior, key);
  const issues =
    savedPresence === 'value' && !Array.isArray(raw)
      ? [
          'The saved collection has an unsupported shape. Its original value is retained.',
        ]
      : [];
  const rows = Array.isArray(raw)
    ? raw.map((item, index) =>
        id === 'timers'
          ? timerRow(item, index, raw)
          : relatedRow(item, index, raw),
      )
    : [];
  return {
    id,
    label: id === 'timers' ? 'Timers' : 'Related work',
    presence: savedPresence,
    state:
      savedPresence !== 'value'
        ? 'absent'
        : issues.length || rows.some((row) => row.state === 'retained-unknown')
          ? 'retained-unknown'
          : rows.length
            ? 'available'
            : 'empty',
    rows,
    issues,
    raw: copy(raw),
  };
}

/** Saved facts only: registry provenance is reported, never recomputed or used as execution authority. */
export function readWorkflowPublishedStepPolicyInspection(
  source: WorkflowPublishedInspectionSource,
  target: { nodeIndex: number; nodeId: string | null },
): WorkflowPublishedStepPolicyInspection {
  const admitted = readWorkflowPublishedInspection(source);
  const unavailable = (
    id: WorkflowPublishedStepPolicySectionId,
  ): WorkflowPublishedStepPolicySection => ({
    id,
    label: id === 'timers' ? 'Timers' : 'Related work',
    state: 'unavailable',
    presence: 'absent',
    rows: [],
    issues: [],
    raw: null,
  });
  const sections: Record<
    WorkflowPublishedStepPolicySectionId,
    WorkflowPublishedStepPolicySection
  > = {
    timers: unavailable('timers'),
    relatedWork: unavailable('relatedWork'),
  };
  const result: WorkflowPublishedStepPolicyInspection = {
    context: JSON.stringify([admitted.context, target]),
    state: 'unavailable',
    provenance: structuredClone(admitted.provenance),
    target: { nodeIndex: target.nodeIndex, nodeId: target.nodeId },
    node: null,
    issues: [...admitted.issues],
    sections,
  };
  if (admitted.state !== 'available') {
    result.issues = [
      ...admitted.issues,
      'Saved timer and related-work content is withheld because the canonical document was not admitted.',
    ];
    return result;
  }
  const document = object(source.definitionDocument),
    nodes = document['nodes'];
  if (
    !Number.isSafeInteger(target.nodeIndex) ||
    target.nodeIndex < 0 ||
    !Array.isArray(nodes) ||
    target.nodeIndex >= nodes.length
  ) {
    result.issues = [
      'The exact saved node index is unavailable. No other step is substituted.',
    ];
    return result;
  }
  const rawNode = nodes[target.nodeIndex],
    node = object(rawNode),
    id = typeof node['id'] === 'string' ? node['id'] : null;
  if (
    !record(rawNode) ||
    id !== target.nodeId ||
    node['typeCode'] !== 'human_step'
  ) {
    result.issues = [
      'The saved human-step index and identity do not match. Policy content is withheld.',
    ];
    return result;
  }
  const unique =
    text(id) && nodes.filter((item) => object(item)['id'] === id).length === 1;
  result.state = 'available';
  result.context = JSON.stringify([admitted.context, target, document]);
  result.issues = unique
    ? []
    : [
        'The saved step ID is missing or duplicated. Indexed facts are retained; cross-references are not guessed.',
      ];
  result.node = {
    index: target.nodeIndex,
    id,
    label:
      typeof node['displayName'] === 'string'
        ? display(node['displayName'])
        : `Saved step ${target.nodeIndex + 1}`,
    typeCode: 'human_step',
    issues: [...result.issues],
    raw: copy(rawNode),
  };
  const declarationRaw = node['declaration'],
    declaration = object(declarationRaw),
    behaviorRaw = declaration['behavior'];
  if (
    (declarationRaw != null && !record(declarationRaw)) ||
    (behaviorRaw != null && !record(behaviorRaw))
  ) {
    for (const key of ['timers', 'relatedWork'] as const)
      sections[key] = {
        ...sections[key],
        state: 'retained-unknown',
        presence: 'value',
        issues: [
          'The saved Work declaration is opaque. No empty collections or generated policies are inferred.',
        ],
        raw: copy(!record(declarationRaw) ? declarationRaw : behaviorRaw),
      };
  } else if (declarationRaw == null || behaviorRaw == null) {
    for (const key of ['timers', 'relatedWork'] as const)
      sections[key] = {
        ...sections[key],
        state: 'absent',
        presence:
          declarationRaw == null
            ? presence(node, 'declaration')
            : presence(declaration, 'behavior'),
        raw: copy(declarationRaw == null ? declarationRaw : behaviorRaw),
      };
  } else {
    sections.timers = section(object(behaviorRaw), 'timers');
    sections.relatedWork = section(object(behaviorRaw), 'relatedWork');
  }
  return result;
}
